import { useState } from 'react';
import { Link } from 'react-router';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Pagination,
  Select,
  toast,
} from '@/components/ui';
import { friendlyMessage, friendlyMessageWithDetails } from '@/lib/errors';
import { TextArea } from './TextArea';
import { useHasRole } from '@/providers/AuthProvider';
import {
  TAX_INVOICE_WRITE_ROLES,
  fetchAllPages,
  fetchOrderTaxInvoiceRequest,
  fetchRequests,
  useTaxInvoiceRequestMutations,
  useTaxInvoiceRequestsQuery,
  type BusinessInfo,
  type IssueResultResponse,
  type RequestRow,
  type TaxInvoiceSearch,
} from '../api/taxInvoiceApi';
import { downloadCsv, formatBusinessNo, formatDateTime, todayKst, toCsv, won } from '../lib/taxInvoiceLib';
import { TaxInvoiceSearchForm, defaultRequestSearch, useTaxInvoiceLabels } from './TaxInvoiceSearchForm';

/*
 * 발행 요청 리스트(고도몰 "세금계산서 발행 요청 리스트") — 검색, 선택 수정·삭제, 일반세금계산서발행, 엑셀.
 * 전자세금계산서발행은 2차(비활성 "준비 중").
 */

const PAGE_SIZES = [10, 20, 50, 100] as const;
const EMPTY_INFO: BusinessInfo = {
  companyName: '', businessNo: '', ceoName: '', businessType: '', businessItem: '', email: '', zipcode: '', address: '', addressDetail: '',
};

export const TaxInvoiceRequestsTab = () => {
  const L = useTaxInvoiceLabels();
  const { t } = L;
  const canWrite = useHasRole(TAX_INVOICE_WRITE_ROLES);
  const [search, setSearch] = useState<TaxInvoiceSearch>(defaultRequestSearch);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState<number>(10);
  const query = useTaxInvoiceRequestsQuery(search, page - 1, size);
  const { update, remove, issue } = useTaxInvoiceRequestMutations();
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());

  const [editing, setEditing] = useState<{ row: RequestRow; info: BusinessInfo; memo: string; loading: boolean } | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);
  const [issueDate, setIssueDate] = useState('');
  const [approvalNo, setApprovalNo] = useState('');
  const [issueResult, setIssueResult] = useState<IssueResultResponse | null>(null);
  const [issueError, setIssueError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const rows = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const selectedRows = rows.filter((r) => selected.has(r.requestId));
  const allOnPage = rows.length > 0 && rows.every((r) => selected.has(r.requestId));

  const resetSelection = () => setSelected(new Set());
  const toggle = (id: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const openEdit = async () => {
    const row = selectedRows[0];
    if (!row) return;
    setEditError(null);
    const fromRow: BusinessInfo = { ...EMPTY_INFO, companyName: row.companyName, businessNo: row.businessNo, ceoName: row.ceoName, businessType: row.businessType, businessItem: row.businessItem, email: row.email, address: row.address };
    setEditing({ row, info: fromRow, memo: row.adminMemo, loading: true });
    try {
      const full = await fetchOrderTaxInvoiceRequest(row.orderId);
      setEditing((e) => (e && e.row.requestId === row.requestId ? { ...e, info: full ?? fromRow, loading: false } : e));
    } catch {
      setEditing((e) => (e ? { ...e, loading: false } : e));
    }
  };

  const saveEdit = () => {
    if (!editing) return;
    const info = editing.info;
    if (!info.companyName.trim() || !info.businessNo.trim() || !info.ceoName.trim()) {
      return setEditError(t('orders.taxInvoice.edit.required', '회사명·사업자번호·대표자는 필수입니다.'));
    }
    if (info.businessNo.replace(/\D/g, '').length !== 10) {
      return setEditError(t('orders.taxInvoice.edit.businessNo', '사업자번호 10자리를 확인해 주세요.'));
    }
    update.mutate(
      { requestId: editing.row.requestId, businessInfo: info, adminMemo: editing.memo },
      {
        onSuccess: () => {
          toast.success(t('orders.taxInvoice.toast.updated', '발행 요청을 수정했습니다.'));
          setEditing(null);
        },
        onError: (e) => setEditError(friendlyMessageWithDetails(e, t('orders.taxInvoice.toast.updateFail', '수정하지 못했습니다.'))),
      },
    );
  };

  const doDelete = () =>
    remove.mutate([...selected], {
      onSuccess: (count) => {
        toast.success(t('orders.taxInvoice.toast.deleted', { defaultValue: '{{count}}건을 삭제했습니다.', count }));
        setConfirmDelete(false);
        resetSelection();
      },
      onError: (e) => toast.error(friendlyMessage(e, t('orders.taxInvoice.toast.deleteFail', '삭제하지 못했습니다.'))),
    });

  const doIssue = () => {
    setIssueError(null);
    issue.mutate(
      { requestIds: [...selected], ...(issueDate ? { issueDate } : {}), ...(approvalNo.trim() ? { approvalNo: approvalNo.trim() } : {}) },
      {
        onSuccess: (res) => {
          setIssueResult(res);
          resetSelection();
        },
        onError: (e) => setIssueError(friendlyMessageWithDetails(e, t('orders.taxInvoice.toast.issueFail', '발행하지 못했습니다.'))),
      },
    );
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const all = await fetchAllPages((p, sz) => fetchRequests(search, p, sz));
      const header = [
        t('orders.taxInvoice.col.no', '번호'), t('orders.taxInvoice.col.requestedAt', '발행요청일'), t('orders.taxInvoice.col.orderNo', '주문번호'),
        t('orders.taxInvoice.col.orderer', '주문자'), t('orders.taxInvoice.col.orderStatus', '주문상태'), t('orders.taxInvoice.col.company', '회사명'),
        t('orders.taxInvoice.col.businessNo', '사업자번호'), t('orders.taxInvoice.col.ceo', '대표자'), t('orders.taxInvoice.col.businessType', '업태'),
        t('orders.taxInvoice.col.businessItem', '종목'), t('orders.taxInvoice.col.email', '이메일'), t('orders.taxInvoice.col.address', '사업장 주소'),
        t('orders.taxInvoice.col.payable', '결제금액'), t('orders.taxInvoice.col.taxType', '과세구분'), t('orders.taxInvoice.col.expectedSupply', '발행예정 공급가액'),
        t('orders.taxInvoice.col.expectedVat', '발행예정 세액'), t('orders.taxInvoice.col.expectedFree', '발행예정 면세'), t('orders.taxInvoice.col.memo', '메모'),
      ];
      const body = all.map((r, i) => [
        i + 1, formatDateTime(r.requestedAt), r.orderNo, r.ordererName, L.orderStatus(r.orderStatus), r.companyName, formatBusinessNo(r.businessNo),
        r.ceoName, r.businessType, r.businessItem, r.email, r.address, r.payableAmount, L.taxType(r.taxType), r.expectedTaxableSupply,
        r.expectedTaxableVat, r.expectedTaxFree, r.adminMemo,
      ]);
      downloadCsv(`세금계산서_발행요청_${todayKst()}.csv`, toCsv(header, body));
    } catch (e) {
      toast.error(friendlyMessage(e, t('orders.taxInvoice.toast.exportFail', '엑셀을 만들지 못했습니다.')));
    } finally {
      setExporting(false);
    }
  };

  const setInfo = (p: Partial<BusinessInfo>) => setEditing((e) => (e ? { ...e, info: { ...e.info, ...p } } : e));

  return (
    <div className="flex flex-col gap-4" data-testid="tax-invoice-requests">
      <TaxInvoiceSearchForm
        initial={search}
        makeDefault={defaultRequestSearch}
        onSearch={(q) => {
          setSearch(q);
          setPage(1);
          resetSelection();
        }}
      />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm" style={{ color: 'var(--content-default)' }} data-testid="tax-invoice-requests-total">
          {t('orders.taxInvoice.total', { defaultValue: '검색 {{total}}건', total: total.toLocaleString('ko-KR') })}
        </span>
        <div className="flex flex-wrap items-center gap-2">
          <Select className="w-24" value={String(size)} onChange={(e) => { setSize(Number(e.target.value)); setPage(1); }} aria-label={t('orders.taxInvoice.pageSize', '표시 개수')}>
            {PAGE_SIZES.map((n) => (
              <option key={n} value={n}>{t('orders.taxInvoice.pageSizeOption', { defaultValue: '{{n}}개', n })}</option>
            ))}
          </Select>
          <Button size="sm" variant="outline" onClick={() => void exportCsv()} disabled={exporting || total === 0} data-testid="tax-invoice-requests-excel">
            {exporting ? t('orders.taxInvoice.exporting', '만드는 중…') : t('orders.taxInvoice.excel', '엑셀 다운로드')}
          </Button>
        </div>
      </div>

      {canWrite ? (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" disabled={selectedRows.length !== 1} onClick={() => void openEdit()} data-testid="tax-invoice-edit">
            {t('orders.taxInvoice.edit.action', '선택 수정')}
          </Button>
          <Button size="sm" variant="outline" disabled={selected.size === 0} onClick={() => setConfirmDelete(true)} data-testid="tax-invoice-delete">
            {t('orders.taxInvoice.delete.action', '선택 삭제')}
          </Button>
          <Button size="sm" variant="primary" disabled={selected.size === 0} onClick={() => { setIssueError(null); setIssueResult(null); setIssueDate(''); setApprovalNo(''); setIssueOpen(true); }} data-testid="tax-invoice-issue">
            {t('orders.taxInvoice.issue.action', '일반세금계산서발행')}
          </Button>
          <Button size="sm" variant="outline" disabled title={t('orders.taxInvoice.electronicSoon', '전자세금계산서는 준비 중입니다.')} data-testid="tax-invoice-issue-electronic">
            {t('orders.taxInvoice.issue.electronic', '전자세금계산서발행 (준비 중)')}
          </Button>
        </div>
      ) : null}

      {query.isError ? (
        <p role="alert" className="text-sm" style={{ color: 'var(--status-danger-fg)' }}>
          {friendlyMessage(query.error, t('orders.taxInvoice.loadError', '목록을 불러오지 못했습니다.'))}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg" style={{ border: '1px solid var(--divider-strong)' }}>
          <table className="w-full min-w-[1100px] text-[13px]" data-testid="tax-invoice-requests-table">
            <thead style={{ background: 'var(--surface-1)', color: 'var(--content-muted)' }}>
              <tr>
                <th className="w-10 px-2 py-2">
                  <Checkbox
                    checked={allOnPage}
                    onCheckedChange={(v) => setSelected(v === true ? new Set(rows.map((r) => r.requestId)) : new Set())}
                    aria-label={t('orders.taxInvoice.selectAll', '전체 선택')}
                  />
                </th>
                <th className="px-2 py-2 text-left">{t('orders.taxInvoice.col.no', '번호')}</th>
                <th className="px-2 py-2 text-left">{t('orders.taxInvoice.col.requestedAt', '발행요청일')}</th>
                <th className="px-2 py-2 text-left">{t('orders.taxInvoice.col.orderNoOrderer', '주문번호 / 주문자')}</th>
                <th className="px-2 py-2 text-left">{t('orders.taxInvoice.col.orderStatus', '주문상태')}</th>
                <th className="px-2 py-2 text-left">{t('orders.taxInvoice.col.businessInfo', '사업자정보')}</th>
                <th className="px-2 py-2 text-right">{t('orders.taxInvoice.col.payable', '결제금액')}</th>
                <th className="px-2 py-2 text-left">{t('orders.taxInvoice.col.taxType', '과세구분')}</th>
                <th className="px-2 py-2 text-right">{t('orders.taxInvoice.col.expected', '발행예정')}</th>
                <th className="px-2 py-2 text-left">{t('orders.taxInvoice.col.memo', '메모')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-2 py-10 text-center" style={{ color: 'var(--content-muted)' }}>
                    {query.isPending ? t('orders.taxInvoice.loading', '불러오는 중…') : t('orders.taxInvoice.empty', '검색된 내역이 없습니다.')}
                  </td>
                </tr>
              ) : (
                rows.map((r, i) => (
                  <tr key={r.requestId} className="border-t align-top" style={{ borderColor: 'var(--divider-soft)' }} data-testid="tax-invoice-request-row">
                    <td className="px-2 py-2 text-center">
                      <Checkbox checked={selected.has(r.requestId)} onCheckedChange={(v) => toggle(r.requestId, v === true)} aria-label={r.orderNo} />
                    </td>
                    <td className="px-2 py-2 tabular-nums">{total - ((page - 1) * size + i)}</td>
                    <td className="whitespace-nowrap px-2 py-2 tabular-nums">{formatDateTime(r.requestedAt)}</td>
                    <td className="px-2 py-2">
                      <Link to={`/orders/${r.orderId}`} className="text-[color:var(--accent-default)] underline">{r.orderNo}</Link>
                      <div style={{ color: 'var(--content-muted)' }}>{r.ordererName}</div>
                    </td>
                    <td className="whitespace-nowrap px-2 py-2">{L.orderStatus(r.orderStatus)}</td>
                    <td className="px-2 py-2">
                      <div style={{ color: 'var(--content-strong)' }}>{r.companyName} · {formatBusinessNo(r.businessNo)}</div>
                      <div style={{ color: 'var(--content-muted)' }}>
                        {r.ceoName}{r.businessType || r.businessItem ? ` · ${[r.businessType, r.businessItem].filter(Boolean).join(' / ')}` : ''}
                      </div>
                      {r.email ? <div style={{ color: 'var(--content-muted)' }}>{r.email}</div> : null}
                      {r.address ? <div style={{ color: 'var(--content-muted)' }}>{r.address}</div> : null}
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums">{won(r.payableAmount)}</td>
                    <td className="px-2 py-2">{L.taxType(r.taxType)}</td>
                    <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums">
                      {r.expectedTaxableSupply + r.expectedTaxableVat > 0 ? (
                        <div>{t('orders.taxInvoice.expectedTaxable', { defaultValue: '과세 {{supply}} + 세액 {{vat}}', supply: won(r.expectedTaxableSupply), vat: won(r.expectedTaxableVat) })}</div>
                      ) : null}
                      {r.expectedTaxFree > 0 ? <div>{t('orders.taxInvoice.expectedFree', { defaultValue: '면세 {{amount}}', amount: won(r.expectedTaxFree) })}</div> : null}
                      {r.expectedTaxableSupply + r.expectedTaxableVat + r.expectedTaxFree === 0 ? '-' : null}
                    </td>
                    <td className="px-2 py-2" style={{ color: 'var(--content-muted)' }}>{r.adminMemo || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
      <Pagination total={total} currentPage={page} pageSize={size} onPageChange={(p) => { setPage(p); resetSelection(); }} showGoTo={false} />

      {/* 선택 수정 — 사업자 정보·메모. 폼은 주문별 신청 정보로 채운다(행에는 우편번호·상세주소가 없다). */}
      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('orders.taxInvoice.edit.title', '발행 요청 수정')}</DialogTitle>
            <DialogDescription>{editing ? `${editing.row.orderNo} · ${editing.row.ordererName}` : null}</DialogDescription>
          </DialogHeader>
          {editing ? (
            <form className="grid grid-cols-2 gap-3 text-sm" onSubmit={(e) => { e.preventDefault(); saveEdit(); }}>
              {([
                ['companyName', t('orders.taxInvoice.col.company', '회사명'), true],
                ['businessNo', t('orders.taxInvoice.col.businessNo', '사업자번호'), true],
                ['ceoName', t('orders.taxInvoice.col.ceo', '대표자'), true],
                ['email', t('orders.taxInvoice.col.email', '이메일'), false],
                ['businessType', t('orders.taxInvoice.col.businessType', '업태'), false],
                ['businessItem', t('orders.taxInvoice.col.businessItem', '종목'), false],
                ['zipcode', t('orders.taxInvoice.col.zipcode', '우편번호'), false],
                ['address', t('orders.taxInvoice.col.address', '사업장 주소'), false],
                ['addressDetail', t('orders.taxInvoice.col.addressDetail', '상세 주소'), false],
              ] as const).map(([key, label, required]) => (
                <label key={key} className={`flex flex-col gap-1 ${key === 'address' || key === 'addressDetail' ? 'col-span-2' : ''}`}>
                  {label}{required ? ' *' : ''}
                  <Input value={editing.info[key]} onChange={(e) => setInfo({ [key]: e.target.value })} disabled={editing.loading} data-testid={`tax-invoice-edit-${key}`} />
                </label>
              ))}
              <label className="col-span-2 flex flex-col gap-1">
                {t('orders.taxInvoice.col.memo', '메모')}
                <TextArea value={editing.memo} maxLength={500} onChange={(e) => setEditing((x) => (x ? { ...x, memo: e.target.value } : x))} data-testid="tax-invoice-edit-memo" />
              </label>
              {editError ? <p role="alert" className="col-span-2 text-sm" style={{ color: 'var(--status-danger-fg)' }}>{editError}</p> : null}
              <DialogFooter className="col-span-2">
                <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(null)}>{t('orders.taxInvoice.close', '닫기')}</Button>
                <Button type="submit" size="sm" variant="primary" disabled={update.isPending || editing.loading} data-testid="tax-invoice-edit-save">
                  {t('orders.taxInvoice.save', '저장')}
                </Button>
              </DialogFooter>
            </form>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* 선택 삭제 */}
      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('orders.taxInvoice.delete.title', '발행 요청 삭제')}</DialogTitle>
            <DialogDescription>
              {t('orders.taxInvoice.delete.description', { defaultValue: '선택한 {{count}}건의 발행 요청을 삭제할까요?', count: selected.size })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(false)}>{t('orders.taxInvoice.close', '닫기')}</Button>
            <Button size="sm" variant="danger" disabled={remove.isPending} onClick={doDelete} data-testid="tax-invoice-delete-confirm">
              {t('orders.taxInvoice.delete.confirm', '삭제')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 일반세금계산서발행 — 홈택스에서 발행한 뒤 기록. 발행일 생략 = 설정 기준일, 결과는 요청별(건너뛴 건 사유). */}
      <Dialog open={issueOpen} onOpenChange={(o) => !o && setIssueOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('orders.taxInvoice.issue.title', '일반세금계산서 발행 기록')}</DialogTitle>
            <DialogDescription>
              {t('orders.taxInvoice.issue.description', '홈택스에서 발행한 뒤 기록합니다. 과세분은 세금계산서, 면세분은 계산서로 각각 기록됩니다. 발행일을 비우면 설정의 발행일 기준(결제완료일·배송완료일)을 씁니다.')}
            </DialogDescription>
          </DialogHeader>
          {issueResult ? (
            <div className="flex flex-col gap-2 text-sm" data-testid="tax-invoice-issue-result">
              <p>{t('orders.taxInvoice.issue.result', { defaultValue: '발행 {{issued}}건 · 건너뜀 {{skipped}}건', issued: issueResult.issuedCount, skipped: issueResult.skippedCount })}</p>
              {issueResult.results.filter((r) => !r.issued).length > 0 ? (
                <ul className="list-disc pl-5" style={{ color: 'var(--status-warn-fg)' }}>
                  {issueResult.results.filter((r) => !r.issued).map((r) => {
                    const row = rows.find((x) => x.requestId === r.requestId);
                    return <li key={r.requestId}>{row?.orderNo ?? r.requestId}: {r.reason ?? '-'}</li>;
                  })}
                </ul>
              ) : null}
              <DialogFooter>
                <Button size="sm" variant="primary" onClick={() => setIssueOpen(false)}>{t('orders.taxInvoice.close', '닫기')}</Button>
              </DialogFooter>
            </div>
          ) : (
            <form className="flex flex-col gap-3 text-sm" onSubmit={(e) => { e.preventDefault(); doIssue(); }}>
              <p>{t('orders.taxInvoice.issue.selected', { defaultValue: '선택 {{count}}건', count: selected.size })}</p>
              <label className="flex flex-col gap-1">
                {t('orders.taxInvoice.issue.issueDate', '발행일 (선택)')}
                <Input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} className="max-w-[200px]" data-testid="tax-invoice-issue-date" />
              </label>
              <label className="flex flex-col gap-1">
                {t('orders.taxInvoice.issue.approvalNo', '승인번호 (선택)')}
                <Input value={approvalNo} maxLength={40} onChange={(e) => setApprovalNo(e.target.value)} data-testid="tax-invoice-issue-approval" />
              </label>
              {issueError ? <p role="alert" style={{ color: 'var(--status-danger-fg)' }}>{issueError}</p> : null}
              <DialogFooter>
                <Button type="button" size="sm" variant="ghost" onClick={() => setIssueOpen(false)}>{t('orders.taxInvoice.close', '닫기')}</Button>
                <Button type="submit" size="sm" variant="primary" disabled={issue.isPending} data-testid="tax-invoice-issue-submit">
                  {t('orders.taxInvoice.issue.submit', '발행 기록')}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
